import { Box, Fade } from "@mui/material";
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';
import MyToastShow from "@/components/Layout/MyToast";
import { SyntheticEvent, createContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from "react-router-dom";
import { useObtenerEmpresasQuery } from "@/redux/api/empresaApi"; // Actualizado
import { IEmpresa } from "@/redux/models/empresaTypes";
import HeaderEmpresa from "./components/HeaderEmpresa";
import { LoadingContentEmpresa } from "./components/LoadingContentEmpresa";
import CarpetasSection from "./sections/CarpetasSection";
import InformacionSection from "./sections/InformacionSection";
import UsuariosSection from "./sections/usuariosSection";

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.success.main,
    height: 3,
    transition: 'all 0.3s ease',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  padding: theme.spacing(1, 2),
  transition: 'color 0.2s ease',
  '&:hover': {
    color: theme.palette.success.light,
  },
  '&.Mui-selected': {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.action.focus,
  },
}));

interface EmpresaContextData {
  empresa?: IEmpresa;
  setEmpresa?: React.Dispatch<React.SetStateAction<IEmpresa | undefined>>;
}

export const EmpresaContext = createContext<EmpresaContextData>({
  empresa: undefined,
  setEmpresa: () => { },
});

const VerEmpresasPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchParams] = useSearchParams();
  const [empresaSelected, setEmpresaSelected] = useState<IEmpresa>();

  // Usar query automática en lugar de lazy
  const { data: empresasData, isLoading, error } = useObtenerEmpresasQuery();
  const listEmpresas = useMemo(() => empresasData?.data || [], [empresasData]);

  useEffect(() => {
    if (error) {
      MyToastShow({ message: 'Error al obtener empresas', type: 'error' });
    }
  }, [error]);

  useEffect(() => {
    if (listEmpresas.length > 0) {
      const id = Number(searchParams.get('empresa'));
      const selected = listEmpresas.find((e) => e.id === id);
      if (selected) setEmpresaSelected(selected);
    }
  }, [listEmpresas, searchParams]);


  const handleChangeTab = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };


  if (isLoading) {
    return (
      <LoadingContentEmpresa />
    );
  }

  return (
    <EmpresaContext.Provider value={{ empresa: empresaSelected, setEmpresa: setEmpresaSelected }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 4 } }}>
        <HeaderEmpresa empresas={listEmpresas} loadingEmpresas={isLoading} />
        {empresaSelected && (
          <Fade in timeout={500}>
            <Box sx={{ mt: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
              <StyledTabs value={tabValue} onChange={handleChangeTab} aria-label="empresa-tabs">
                <StyledTab label="Carpetas" />
                <StyledTab label="Información" />
                <StyledTab label="Usuarios" />
              </StyledTabs>
              <Box sx={{ p: 3 }}>
                <Fade in={tabValue === 0} timeout={300}>
                  <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
                    {tabValue === 0 && <CarpetasSection />
                      // Esto hace que cada vez que se cambie de tab se vuelva a cargar el componente y actualizar las carpetas
                      // Ademas de una mejora de rendimiento
                    }
                  </Box>
                </Fade>
                <Fade in={tabValue === 1} timeout={300}>
                  <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
                    <InformacionSection />
                  </Box>
                </Fade>
                <Fade in={tabValue === 2} timeout={300}>
                  <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
                    <UsuariosSection />
                  </Box>
                </Fade>
              </Box>
            </Box>
          </Fade>
        )}
      </Box>
    </EmpresaContext.Provider>
  );
};

export default VerEmpresasPage;